import type { StreamDecoderMetrics } from '../ws/stream-decoder.ts';

export interface Tile {
  element: HTMLElement;
  video: HTMLVideoElement;
  streamId: string;
  isMine: boolean;
  watching: boolean;
  expanded: boolean;
  selectionLocked: boolean;
  showId: boolean;
  volume: number;
  // Metricas de playback medidas na camada de exibicao (<video>): stalls sao
  // gaps >300ms entre frames apresentados (a mesma tecnica usada no
  // test-whep.html, pra comparacao ws vs WHEP apples-to-apples).
  playback: PlaybackProbe;
  setStreamId: (streamId: string) => void;
  setViewerCount: (count: number) => void;
  setMetrics: (metrics: StreamDecoderMetrics | null) => void;
  setMuted: (muted: boolean) => void;
  setWatching: (watching: boolean) => void;
  setLabel: (label: string) => void;
  setExpanded: (expanded: boolean) => void;
  setShowId: (show: boolean) => void;
  setSpinner: (visible: boolean) => void;
  updateChart: (history: Array<{ fps: number; bitrate: number; latency?: number; dropped: number; pacingStdDev?: number }>) => void;
  destroy: () => void;
  onWatch?: () => void;
  onWatchStop?: () => void;
  onMuteToggle?: () => void;
  onShowIdToggle?: () => void;
  onVolumeChange?: (volume: number) => void;
  onContextMenu?: (x: number, y: number) => void;
}

const ICON_VOLUME_ON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none"></polygon><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path><path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path></svg>`;
const ICON_VOLUME_OFF = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none"></polygon><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line></svg>`;
const ICON_EYE = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path fill="currentColor" d="M12 5c-6.6 0-10 5.4-10 7s3.4 7 10 7 10-5.4 10-7-3.4-7-10-7zm0 12c-2.8 0-5-2.2-5-5s2.2-5 5-5 5 2.2 5 5-2.2 5-5 5z"/><circle cx="12" cy="12" r="2.5" fill="currentColor"/></svg>`;
const ICON_SCREEN = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" stroke-width="2"/><path d="M8 21h8M12 18v3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;

// Contadores de playback da camada de exibicao. Stall = gap > STALL_MS entre
// frames apresentados (rVFC): o video ficou congelado esperando conteudo.
// Usado pelo __viewerPerf e comparado com o medidor do test-whep.html.
export interface PlaybackProbe {
  presented: number;
  stalls: number;
  maxStallMs: number;
  fps: number;
}
const PLAYBACK_STALL_MS = 300;

export function createTile(
  id: string,
  label: string,
  isMine = false,
  onContextMenu?: (x: number, y: number) => void
): Tile {
  const tile = document.createElement('div');
  tile.className = 'tile' + (isMine ? ' mine' : '');
  tile.dataset.streamId = id;

  const video = document.createElement('video');
  video.className = 'tile-video';
  video.autoplay = true;
  video.playsInline = true;
  video.muted = true;

  // Monitor de playback via requestVideoFrameCallback: conta frames
  // apresentados, stalls (gap >300ms entre apresentacoes = video congelado
  // esperando conteudo) e fps de exibicao. Mesma tecnica do test-whep.html.
  const playbackProbe: PlaybackProbe = { presented: 0, stalls: 0, maxStallMs: 0, fps: 0 };
  let rVfcLastAt = 0;
  let rVfcWindowStart = 0;
  let rVfcWindowCount = 0;
  const onPresentedFrame: VideoFrameRequestCallback = (now) => {
    playbackProbe.presented++;
    if (rVfcLastAt > 0) {
      const gap = now - rVfcLastAt;
      if (gap > PLAYBACK_STALL_MS) {
        playbackProbe.stalls++;
        if (gap > playbackProbe.maxStallMs) playbackProbe.maxStallMs = gap;
        if (gap > 500) console.warn(`[tile ${id}] playback stall ${gap.toFixed(0)}ms (total ${playbackProbe.stalls})`);
      }
    }
    rVfcLastAt = now;
    rVfcWindowCount++;
    if (rVfcWindowStart === 0) rVfcWindowStart = now;
    if (now - rVfcWindowStart >= 1000) {
      playbackProbe.fps = Math.round((rVfcWindowCount * 1000) / (now - rVfcWindowStart));
      rVfcWindowStart = now;
      rVfcWindowCount = 0;
    }
    rVfcHandle = video.requestVideoFrameCallback(onPresentedFrame);
  };
  let rVfcHandle = video.requestVideoFrameCallback(onPresentedFrame);

  const watchOverlay = document.createElement('div');
  watchOverlay.className = 'watch-overlay';
  watchOverlay.innerHTML = `
    <div class="watch-icon">${ICON_EYE}</div>
    <div class="watch-text">WATCH</div>
  `;

  const viewerCount = document.createElement('div');
  viewerCount.className = 'viewer-count';
  viewerCount.innerHTML = `${ICON_EYE} <span>0</span>`;

  const name = document.createElement('div');
  name.className = 'name';
  name.innerHTML = `${ICON_SCREEN}<span>${escapeHtml(label)}</span>`;

  const overlayBottom = document.createElement('div');
  overlayBottom.className = 'overlay-bottom';
  overlayBottom.appendChild(name);

  const stats = document.createElement('div');
  stats.className = 'tile-stats';

  const statsText = document.createElement('div');
  statsText.className = 'tile-stats-text';
  stats.appendChild(statsText);

  // Cria as linhas do painel de stats uma unica vez e atualiza textContent
  // dos spans individuais. innerHTML recriava elementos a cada segundo e
  // fazia o contador de DOM nodes subir ate o GC.
  type LabeledValue = { label: HTMLSpanElement; val: HTMLSpanElement; wrap: HTMLSpanElement };
  type StatRow = {
    val: HTMLSpanElement;
    optional?: Record<string, LabeledValue>;
    [key: string]: HTMLSpanElement | LabeledValue | Record<string, LabeledValue> | undefined;
  };
  const statValues: Record<string, StatRow> = {};

  function makeLabel(text: string) {
    const s = document.createElement('span');
    s.className = 'label';
    s.textContent = text;
    return s;
  }
  function makeValue(id: string) {
    const s = document.createElement('span');
    s.className = `value stat-${id}`;
    return s;
  }
  function addLabeledValue(row: HTMLElement, id: string, labelText: string): LabeledValue {
    const wrap = document.createElement('span');
    wrap.className = `stat-opt stat-opt-${id}`;
    wrap.style.display = 'none';
    const label = makeLabel(labelText);
    const val = makeValue(id);
    wrap.appendChild(label);
    wrap.appendChild(val);
    row.appendChild(wrap);
    return { label, val, wrap };
  }

  function ensureStatRows() {
    if (statsText.children.length > 0) return;
    const mkRow = (id: string) => {
      const div = document.createElement('div');
      div.dataset.statRow = id;
      statsText.appendChild(div);
      return div;
    };
    {
      const row = mkRow('res');
      row.appendChild(makeLabel('res: '));
      statValues.res = { val: makeValue('res') };
      row.appendChild(statValues.res.val);
    }
    {
      const row = mkRow('fps');
      row.appendChild(makeLabel('fps: '));
      statValues.fps = { val: makeValue('fps') };
      row.appendChild(statValues.fps.val);
      row.appendChild(makeLabel(' | bitrate: '));
      statValues.fps.bitrate = makeValue('fps-bitrate');
      row.appendChild(statValues.fps.bitrate);
    }
    {
      const row = mkRow('frames');
      row.appendChild(makeLabel('frames: '));
      const val = makeValue('frames');
      statValues.frames = { val, optional: {} };
      row.appendChild(val);
      row.appendChild(makeLabel(' | dropped: '));
      statValues.frames.optional!.dropped = { label: row.lastChild as HTMLSpanElement, val: makeValue('frames-dropped'), wrap: row.lastChild as HTMLSpanElement };
      row.appendChild(statValues.frames.optional!.dropped.val);
      statValues.frames.optional!.pending = addLabeledValue(row, 'frames-pending', ' | pending: ');
      statValues.frames.optional!.encoded = addLabeledValue(row, 'frames-encoded', ' | enc: ');
      statValues.frames.optional!.decoded = addLabeledValue(row, 'frames-decoded', ' | dec: ');
      // dropped é sempre visível
      statValues.frames.optional!.dropped.wrap.style.display = 'inline';
    }
    {
      const row = mkRow('latency');
      row.appendChild(makeLabel('latencia: '));
      const val = makeValue('latency');
      statValues.latency = { val, optional: {} };
      row.appendChild(val);
      statValues.latency.optional!.peak = addLabeledValue(row, 'latency-peak', ' (pico ');
      statValues.latency.optional!.peak.wrap.appendChild(document.createTextNode('ms)'));
      statValues.latency.optional!.jb = addLabeledValue(row, 'latency-jb', ' | jb: ');
      statValues.latency.optional!.jb.wrap.appendChild(document.createTextNode('ms'));
    }
    {
      const row = mkRow('codec');
      row.appendChild(makeLabel('codec: '));
      const val = makeValue('codec');
      statValues.codec = { val, optional: {} };
      row.appendChild(val);
      statValues.codec.optional!.hw = addLabeledValue(row, 'codec-hw', ' · ');
      statValues.codec.optional!.failures = addLabeledValue(row, 'codec-failures', ' | falhas: ');
    }
    {
      const row = mkRow('render');
      row.appendChild(makeLabel('render: '));
      const val = makeValue('render');
      statValues.render = { val, optional: {} };
      row.appendChild(val);
      statValues.render.optional!.dropped = addLabeledValue(row, 'render-dropped', ' · drop ');
      statValues.render.optional!.memory = addLabeledValue(row, 'render-memory', ' · mem ');
      statValues.render.optional!.memory.wrap.appendChild(document.createTextNode('MB'));
    }
    {
      const row = mkRow('pacing');
      row.appendChild(makeLabel('pacing: '));
      statValues.pacing = { val: makeValue('pacing') };
      row.appendChild(statValues.pacing.val);
    }
  }

  function setOpt(rowId: string, key: string, visible: boolean, text?: string) {
    const row = statValues[rowId];
    if (!row || !row.optional) return;
    const lv = row.optional[key];
    if (!lv) return;
    lv.wrap.style.display = visible ? 'inline' : 'none';
    if (visible && text !== undefined) lv.val.textContent = text;
  }

  const statsChart = document.createElement('canvas');
  statsChart.className = 'tile-stats-chart';
  statsChart.width = 200;
  statsChart.height = 60;
  stats.appendChild(statsChart);

  const statsLegend = document.createElement('div');
  statsLegend.className = 'tile-stats-legend';
  statsLegend.innerHTML = `
    <span><span class="dot" style="background:#5865f2"></span>bitrate</span>
    <span><span class="dot" style="background:#ed4245"></span>latência</span>
    <span><span class="dot" style="background:#3ba55d"></span>pacing (std)</span>
  `;
  stats.appendChild(statsLegend);

  type MetricPoint = { fps: number; bitrate: number; latency?: number; dropped: number; pacingStdDev?: number };
  let chartHistory: MetricPoint[] = [];
  let previousChartHistory: MetricPoint[] = [];
  let chartAnimation: number | null = null;
  const CHART_ANIMATION_MS = 500;

  function drawAnimatedChart(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    current: MetricPoint[],
    previous: MetricPoint[]
  ) {
    ctx.clearRect(0, 0, w, h);
    if (current.length < 2) return;

    const maxBitrate = Math.max(1, ...current.map((p) => p.bitrate));
    const maxMs = Math.max(
      1,
      ...current.map((p) => Math.max(p.latency ?? 0, p.pacingStdDev ?? 0))
    );

    const prevLen = previous.length;
    const currLen = current.length;

    function getXOld(i: number): number {
      if (prevLen < 2) return w;
      if (currLen === prevLen) {
        if (i + 1 < prevLen) return ((i + 1) / (prevLen - 1)) * w;
        return w;
      }
      if (currLen === prevLen + 1) {
        if (i < prevLen) return (i / (prevLen - 1)) * w;
        return w;
      }
      return (i / (currLen - 1)) * w;
    }

    const drawLine = (values: number[], color: string, maxValue: number, progress: number) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 0; i < values.length; i++) {
        const xNew = (i / (currLen - 1)) * w;
        const xOld = getXOld(i);
        const x = xOld + (xNew - xOld) * progress;
        const y = h - (values[i] / maxValue) * h;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    };

    return { maxBitrate, maxMs, drawLine };
  }

  const muteBtn = document.createElement('button');
  muteBtn.className = 'mute-btn';
  muteBtn.innerHTML = ICON_VOLUME_ON;
  muteBtn.title = 'Silenciar';

  const spinner = document.createElement('div');
  spinner.className = 'tile-spinner';
  spinner.innerHTML = '<div class="spinner"></div>';

  tile.appendChild(video);
  tile.appendChild(spinner);
  tile.appendChild(watchOverlay);
  tile.appendChild(viewerCount);
  tile.appendChild(overlayBottom);
  tile.appendChild(stats);
  tile.appendChild(muteBtn);

  const tileObj: Tile = {
    element: tile,
    video,
    streamId: id,
    isMine,
    watching: false,
    expanded: false,
    selectionLocked: false,
    playback: playbackProbe,
    showId: true,
    volume: 1,
    setStreamId: (newId) => {
      tileObj.streamId = newId;
      tile.dataset.streamId = newId;
    },
    setViewerCount: (count) => {
      const span = viewerCount.querySelector('span');
      if (span) span.textContent = String(count);
    },
    setMetrics: (metrics) => {
      if (!metrics) {
        stats.classList.remove('active');
        return;
      }
      ensureStatRows();
      const pacing = metrics.pacing;

      statValues.res.val.textContent = metrics.resolution;
      statValues.fps.val.textContent = metrics.fps.toFixed(1);
      (statValues.fps.bitrate as HTMLSpanElement).textContent = formatBitrate(metrics.bitrate);

      statValues.frames.val.textContent = String(metrics.decodedFrames);
      statValues.frames.optional!.dropped.val.textContent = String(metrics.droppedFrames);
      setOpt('frames', 'pending', metrics.pendingDecodes !== undefined, String(metrics.pendingDecodes));
      setOpt('frames', 'encoded', metrics.encodedQueueSize !== undefined, String(metrics.encodedQueueSize));
      setOpt('frames', 'decoded', metrics.decodedQueueSize !== undefined, String(metrics.decodedQueueSize));

      if (metrics.latency !== undefined) {
        statValues.latency.val.textContent = `${metrics.latency}ms`;
        setOpt('latency', 'peak', metrics.latencyPeak !== undefined, String(metrics.latencyPeak));
        setOpt('latency', 'jb', metrics.jitterBufferMs !== undefined, String(metrics.jitterBufferMs));
      } else {
        statValues.latency.val.textContent = '-';
        setOpt('latency', 'peak', false);
        setOpt('latency', 'jb', false);
      }

      if (metrics.codec) {
        statValues.codec.val.textContent = `${metrics.codecLabel ?? metrics.codec} · ${metrics.codec}`;
        setOpt('codec', 'hw', !!metrics.hardwareAcceleration, metrics.hardwareAcceleration === 'hardware' ? 'hw' : 'sw');
        setOpt('codec', 'failures', (metrics.decoderFailures ?? 0) > 0, String(metrics.decoderFailures));
      } else {
        statValues.codec.val.textContent = '';
        setOpt('codec', 'hw', false);
        setOpt('codec', 'failures', false);
      }

      if (metrics.renderFps !== undefined) {
        statValues.render.val.textContent = `${metrics.renderFps.toFixed(1)} fps · ${(metrics.renderTimeMs ?? 0).toFixed(2)}ms · fila ${metrics.renderQueue ?? 0}`;
        setOpt('render', 'dropped', (metrics.renderDropped ?? 0) > 0, String(metrics.renderDropped));
        setOpt('render', 'memory', !!metrics.memoryMB, String(metrics.memoryMB));
      } else {
        statValues.render.val.textContent = '';
        setOpt('render', 'dropped', false);
        setOpt('render', 'memory', false);
      }

      statValues.pacing.val.textContent = (pacing
        ? `avg=${pacing.avgDeltaMs.toFixed(1)}ms std=${pacing.stdDevMs.toFixed(1)}ms min=${pacing.minDeltaMs.toFixed(0)} max=${pacing.maxDeltaMs.toFixed(0)}`
        : '') + ` · pb ${playbackProbe.fps}fps ${playbackProbe.stalls}st max${Math.round(playbackProbe.maxStallMs)}ms`;
    },
    setMuted: (muted) => {
      muteBtn.innerHTML = muted ? ICON_VOLUME_OFF : ICON_VOLUME_ON;
      muteBtn.title = muted ? 'Ativar som' : 'Silenciar';
      tile.classList.toggle('muted', muted);
    },
    setWatching: (watching) => {
      tileObj.watching = watching;
      watchOverlay.style.display = watching ? 'none' : 'flex';
      viewerCount.style.display = watching ? 'flex' : 'none';
      if (watching) {
        // Ao iniciar/retomar watching, mostra spinner e esconde o video ate
        // o primeiro frame decodificado chegar, evitando ver imagem antiga
        // ou artefatos sob o loading.
        spinner.style.display = 'flex';
        video.style.display = 'none';
      } else {
        spinner.style.display = 'none';
        video.style.display = 'none';
        stats.classList.remove('active');
        video.pause();
      }
    },
    setSpinner: (visible) => {
      if (!tileObj.watching) return;
      spinner.style.display = visible ? 'flex' : 'none';
      video.style.display = visible ? 'none' : 'block';
    },
    updateChart: (history) => {
      const chart = stats.querySelector('.tile-stats-chart') as HTMLCanvasElement | null;
      if (!chart) return;
      const ctx = chart.getContext('2d');
      if (!ctx) return;
      const w = chart.width;
      const h = chart.height;

      if (chartAnimation) cancelAnimationFrame(chartAnimation);

      previousChartHistory = chartHistory;
      chartHistory = history.slice();

      if (chartHistory.length < 2) {
        ctx.clearRect(0, 0, w, h);
        return;
      }

      const prevLen = previousChartHistory.length;
      const currLen = chartHistory.length;
      const isContinuous =
        prevLen >= 2 &&
        (currLen === prevLen || currLen === prevLen + 1) &&
        previousChartHistory[prevLen - 1] === chartHistory[currLen - (currLen === prevLen ? 0 : 1) - 1];

      if (!isContinuous) {
        previousChartHistory = chartHistory.slice();
      }

      const start = performance.now();
      const animate = (now: number) => {
        const elapsed = now - start;
        const progress = Math.min(1, elapsed / CHART_ANIMATION_MS);

        const renderer = drawAnimatedChart(ctx, w, h, chartHistory, previousChartHistory);
        if (renderer) {
          renderer.drawLine(
            chartHistory.map((p) => p.bitrate),
            '#5865f2',
            renderer.maxBitrate,
            progress
          );
          renderer.drawLine(
            chartHistory.map((p) => p.latency ?? 0),
            '#ed4245',
            renderer.maxMs,
            progress
          );
          renderer.drawLine(
            chartHistory.map((p) => p.pacingStdDev ?? 0),
            '#3ba55d',
            renderer.maxMs,
            progress
          );
        }

        if (progress < 1) {
          chartAnimation = requestAnimationFrame(animate);
        }
      };

      chartAnimation = requestAnimationFrame(animate);
    },
    setLabel: (newLabel) => {
      name.innerHTML = `${ICON_SCREEN}<span>${escapeHtml(newLabel)}</span>`;
    },
    setExpanded: (expanded) => {
      tileObj.expanded = expanded;
      tile.classList.toggle('expanded', expanded);
    },
    setShowId: (show) => {
      tileObj.showId = show;
      tile.classList.toggle('show-id', show);
    },
    destroy: () => {
      if (chartAnimation) cancelAnimationFrame(chartAnimation);
      try {
        video.cancelVideoFrameCallback(rVfcHandle);
      } catch {
        /* ignore */
      }
      try {
        video.pause();
        video.srcObject = null;
        video.load();
      } catch {
        /* ignore */
      }
      tile.remove();
    },
  };

  tileObj.setWatching(false);
  tileObj.setShowId(true);
  tileObj.setMuted(true);

  let muted = true;

  muteBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    muted = !muted;
    tileObj.setMuted(muted);
    tileObj.onMuteToggle?.();
  });

  watchOverlay.addEventListener('click', (e) => {
    e.stopPropagation();
    tileObj.onWatch?.();
  });

  tile.addEventListener('click', () => {
    if (tileObj.watching && !tileObj.selectionLocked) {
      tileObj.setExpanded(!tileObj.expanded);
    }
  });

  tile.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    e.stopPropagation();
    onContextMenu?.(e.clientX, e.clientY);
  });

  return tileObj;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function formatBitrate(bps: number): string {
  if (!bps) return '0 kbps';
  if (bps >= 1e6) return (bps / 1e6).toFixed(2) + ' Mbps';
  return (bps / 1e3).toFixed(0) + ' kbps';
}
